import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Layers, Zap, Grid3X3, Activity, Brain, Cpu, 
  Search, Loader2, FileText, ArrowRight, Sparkles
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  layers: Layers,
  zap: Zap,
  grid: Grid3X3,
  activity: Activity,
  brain: Brain,
  cpu: Cpu,
};

const complexityColors = {
  low: "bg-[#00ff9d]/10 text-[#00ff9d] border-[#00ff9d]/30",
  medium: "bg-[#00f0ff]/10 text-[#00f0ff] border-[#00f0ff]/30",
  high: "bg-[#7000ff]/10 text-[#7000ff] border-[#7000ff]/30",
};

const categoryLabels = {
  web: "Web",
  api: "API",
  enterprise: "Enterprise",
  data: "Data",
  ai: "AI/ML",
  iot: "IoT",
};

export const TemplateSelector = ({ onSelectTemplate, onStartBlank }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loadingTemplate, setLoadingTemplate] = useState(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
    } catch (error) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = async (template) => {
    try {
      setLoadingTemplate(template.id);
      // Fetch the full template with phase1 data
      const response = await axios.get(`${API}/templates/${template.id}`);
      onSelectTemplate(response.data);
    } catch (error) {
      toast.error("Failed to load template details");
    } finally {
      setLoadingTemplate(null);
    }
  };

  const filteredTemplates = templates.filter((template) => {
    const matchesSearch = 
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = ["all", ...new Set(templates.map((t) => t.category))];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#00f0ff] animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-10">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
          Choose Your Starting Point
        </h1>
        <p className="text-zinc-400 max-w-2xl mx-auto">
          Start with a pre-configured template for common architectures or begin from scratch
        </p>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-black/50 border-white/10 text-white focus:border-[#00f0ff]/50"
            data-testid="template-search-input"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {categories.map((cat) => (
            <Button
              key={cat}
              variant={selectedCategory === cat ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(cat)}
              className={
                selectedCategory === cat
                  ? "bg-[#00f0ff] text-black hover:bg-[#00f0ff]/90"
                  : "border-zinc-700 text-zinc-300 hover:bg-white/5"
              }
              data-testid={`category-filter-${cat}`}
            >
              {cat === "all" ? "All" : categoryLabels[cat] || cat}
            </Button>
          ))}
        </div>
      </div>

      {/* Blank Project Option */}
      <Card 
        className="bg-gradient-to-r from-[#09090b] to-[#0a0a0f] border-dashed border-white/10 hover:border-[#00f0ff]/30 transition-all cursor-pointer mb-8 group"
        onClick={onStartBlank}
        data-testid="start-blank-project"
      >
        <CardContent className="p-6 flex items-center gap-6">
          <div className="w-14 h-14 rounded-sm bg-zinc-900 border border-zinc-800 flex items-center justify-center group-hover:border-[#00f0ff]/30 transition-colors">
            <FileText className="w-7 h-7 text-zinc-500 group-hover:text-[#00f0ff] transition-colors" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white group-hover:text-[#00f0ff] transition-colors">
              Start from Scratch
            </h3>
            <p className="text-sm text-zinc-500">
              Build your custom architecture with full control over every detail
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-[#00f0ff] transition-colors" />
        </CardContent>
      </Card>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemplates.map((template) => {
          const IconComponent = iconMap[template.icon] || Layers;
          return (
            <Card
              key={template.id}
              className="bg-[#09090b] border-white/5 hover:border-[#00f0ff]/30 transition-all cursor-pointer group"
              onClick={() => handleSelectTemplate(template)}
              data-testid={`template-card-${template.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-sm bg-[#00f0ff]/10 border border-[#00f0ff]/20 flex items-center justify-center group-hover:bg-[#00f0ff]/20 transition-colors">
                    <IconComponent className="w-6 h-6 text-[#00f0ff]" />
                  </div>
                  <div className="flex gap-2">
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${complexityColors[template.complexity]}`}
                    >
                      {template.complexity}
                    </Badge>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[#00f0ff] transition-colors">
                  {template.name}
                </h3>
                <p className="text-sm text-zinc-500 mb-4 line-clamp-2">
                  {template.description}
                </p>

                <div className="flex items-center justify-between pt-4 border-t border-white/5">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-zinc-600">Est. Cost:</span>
                    <span className="text-xs text-[#00ff9d] font-mono">
                      {template.estimated_cost}
                    </span>
                  </div>
                  <Button
                    size="sm"
                    className="bg-zinc-800 text-white hover:bg-[#00f0ff] hover:text-black transition-colors"
                    data-testid={`use-template-${template.id}`}
                    disabled={loadingTemplate === template.id}
                  >
                    {loadingTemplate === template.id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3 mr-1" />
                        Use
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <p className="text-zinc-500">No templates match your search</p>
        </div>
      )}
    </div>
  );
};
